/**
 * System workspace document access.
 *
 * Sharded databases keep their schema, title and manifest in a workspace-level
 * system document (`db$<workspace>$database$<id>$root`). Every consumer that
 * needs the storage block of a database resolves that document through this
 * module so registration, loading and schema propagation stay in one place.
 *
 * @module system-workspace/system-workspace-store
 */

import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import type { Doc, Store } from '@blocksuite/store';

/**
 * Resolve the workspace document for a system doc id, registering it with the
 * workspace when it is not known yet.
 */
export function ensureSystemWorkspaceDoc(hostDoc: Store, docId: string): Doc {
  let doc = hostDoc.workspace.getDoc(docId);
  if (doc) return doc;

  const registerDoc = (
    hostDoc.workspace as { registerDoc?: (id: string) => void }
  ).registerDoc;
  if (registerDoc) {
    registerDoc.call(hostDoc.workspace, docId);
    doc = hostDoc.workspace.getDoc(docId);
    if (doc) return doc;
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Failed to register database system document "${docId}".`
    );
  }

  return hostDoc.workspace.createDoc(docId);
}

/**
 * Load a system workspace document and return its store with every block
 * schema of the host store registered.
 */
export function getSystemWorkspaceStore(hostDoc: Store, docId: string): Store {
  return openSystemWorkspaceStore(
    hostDoc,
    ensureSystemWorkspaceDoc(hostDoc, docId),
    docId
  );
}

/**
 * Like {@link getSystemWorkspaceStore}, but never registers or creates the
 * system document. Returns `undefined` when the workspace does not know it.
 */
export function peekSystemWorkspaceStore(
  hostDoc: Store,
  docId: string
): Store | undefined {
  const doc = hostDoc.workspace.getDoc(docId);
  if (!doc) return undefined;
  return openSystemWorkspaceStore(hostDoc, doc, docId);
}

function openSystemWorkspaceStore(
  hostDoc: Store,
  docRecord: Doc,
  docId: string
): Store {
  docRecord.load();
  const systemStore = docRecord.getStore({ id: docId });
  if (!hostDoc.schema) return systemStore;
  const missingSchemas = Array.from(
    hostDoc.schema.flavourSchemaMap.values()
  ).filter(
    schema => !systemStore.schema.flavourSchemaMap.has(schema.model.flavour)
  );
  if (missingSchemas.length > 0) {
    systemStore.schema.register(missingSchemas);
  }
  return systemStore;
}
